/**
 * The vehicle pool of פלוגת שינוע 99 — the twenty-eight vehicles the company
 * actually holds, seeded into the `vehicles` table on first run.
 *
 * This is real unit data, exactly as `src/core/seed-soldiers.ts` is for the
 * roster: `src/core/mock-fleet.ts` remains the verification-only fixture set
 * that no application module may import.
 *
 * Two doors, the same pair the roster uses:
 *
 * - `seedVehicleRoster()` writes the list into an *empty* table, so an edited
 *   or deleted vehicle is never resurrected on the next reload.
 * - `reconcileVehicleRoster()` is the forced migration for a device that was
 *   already running before the pool shipped. It backfills by מספר צ and adds
 *   nothing else — the pool has never been seeded with demo records, so
 *   whatever a device already holds is the unit's own and is left alone.
 *
 * Ids are derived from the plate (`veh-60427503`), which keeps the seed stable
 * across reloads and clear of the `veh-101`-style fixture ids.
 *
 * The three-digit internal number (מספר פנימי) is *not* in the handover sheet.
 * A seeded vehicle therefore carries an empty `militaryId` — "not recorded" —
 * and is identified by its plate until the unit paints the number in from the
 * vehicle form. That is why `db.version(11)` no longer keys the table on a
 * unique internal number; uniqueness is enforced in the store instead, over
 * the numbers that are actually recorded.
 */
import type { Vehicle, VehicleType, WeightCategory } from '../types/fleet';
import { HOME_COMPANY, HOME_COMPANY_FULL, isHomeCompany } from './vehicle-location';

/** The moment the pool was filed — a fixed stamp, so the seed is stable. */
const SEEDED_AT = '2026-01-01T08:00:00.000Z';

/** What a model is, in licensing and in filing terms. Keyed by model name. */
const MODEL_SPECS: Record<string, { vehicleType: VehicleType; weightCategory: WeightCategory }> = {
  "דודג' ראם": { vehicleType: 'van', weightCategory: 'light' },
  'טויוטה היילקס': { vehicleType: 'van', weightCategory: 'light' },
  'איסוזו דימקס': { vehicleType: 'van', weightCategory: 'light' },
  'סאנגיונג מוסו': { vehicleType: 'van', weightCategory: 'light' },
  'סיטרואן ברלינגו': { vehicleType: 'van', weightCategory: 'light' },
  'פוטון': { vehicleType: 'truck', weightCategory: 'medium' },
  'פולקסווגן אמארוק': { vehicleType: 'van', weightCategory: 'light' },
  'מיצובישי טריטון': { vehicleType: 'van', weightCategory: 'light' },
  'FMTV': { vehicleType: 'truck', weightCategory: 'heavy' },
  'אושקוש': { vehicleType: 'truck', weightCategory: 'heavy' },
  'ריאו פלטה': { vehicleType: 'flatbed', weightCategory: 'heavy' },
  'ריאו סולר': { vehicleType: 'tanker', weightCategory: 'heavy' },
  'האמר': { vehicleType: 'van', weightCategory: 'light' },
};

/** One line of the pool as the company keeps it: model, מספר צ, who holds it. */
interface PoolEntry {
  model: string;
  /** Full military plate, exactly as the handover sheet writes it. */
  plateNumber: string;
  /** The unit holding the vehicle. Omitted means it is with us. */
  assignedCompany?: string;
}

/** רכבי יר"ם — the operational pool. */
const YARAM_POOL: readonly PoolEntry[] = [
  { model: "דודג' ראם", plateNumber: '604-27-503' },
  { model: "דודג' ראם", plateNumber: '803-37-203' },
  { model: 'טויוטה היילקס', plateNumber: '464-27-601' },
  { model: 'טויוטה היילקס', plateNumber: '829-80-902', assignedCompany: 'פלק"ד' },
  { model: 'טויוטה היילקס', plateNumber: '342-58-504' },
  { model: 'טויוטה היילקס', plateNumber: '569-98-003', assignedCompany: 'פלק"ד' },
  { model: 'איסוזו דימקס', plateNumber: '804-22-602', assignedCompany: 'פלק"ד' },
  { model: 'איסוזו דימקס', plateNumber: '377-33-903', assignedCompany: 'מ.צ' },
  { model: 'איסוזו דימקס', plateNumber: '899-503', assignedCompany: 'מ.צ' },
  { model: 'סאנגיונג מוסו', plateNumber: '179-22-204', assignedCompany: 'מ.צ' },
  { model: 'סיטרואן ברלינגו', plateNumber: '890-86-103', assignedCompany: 'סמג"ד' },
  { model: 'סיטרואן ברלינגו', plateNumber: '880-90-103' },
  { model: 'פוטון', plateNumber: '384-85-104', assignedCompany: 'חש"ל' },
  { model: 'פולקסווגן אמארוק', plateNumber: '609-86-803', assignedCompany: 'מג"ד' },
  { model: 'מיצובישי טריטון', plateNumber: '281-904' },
];

/** רכבים חומים — the administrative pool. All of it stands with us. */
const BROWN_POOL: readonly PoolEntry[] = [
  { model: 'FMTV', plateNumber: '990-083' },
  { model: 'FMTV', plateNumber: '990-108' },
  { model: 'FMTV', plateNumber: '990-766' },
  { model: 'אושקוש', plateNumber: '627-284' },
  { model: 'אושקוש', plateNumber: '627-302' },
  { model: 'אושקוש', plateNumber: '627-017' },
  { model: 'ריאו פלטה', plateNumber: '679-354' },
  { model: 'ריאו פלטה', plateNumber: '679-358' },
  { model: 'ריאו פלטה', plateNumber: '681-017' },
  { model: 'ריאו סולר', plateNumber: '675-527' },
  { model: 'האמר', plateNumber: '705-911' },
  { model: 'האמר', plateNumber: '706-337' },
  { model: 'האמר', plateNumber: '705-918' },
];

/** The digits of a plate, which is what identifies a vehicle here. */
export const plateKey = (plateNumber: string): string =>
  String(plateNumber ?? '').replace(/\D/g, '');

/** The stable id a pool record is written under. */
export const vehicleSeedId = (plateNumber: string): string => `veh-${plateKey(plateNumber)}`;

function toVehicle(entry: PoolEntry, category: 'yaram' | 'brown'): Vehicle {
  const spec = MODEL_SPECS[entry.model];
  if (!spec) throw new Error(`No specification filed for model "${entry.model}"`);
  const assignedCompany = entry.assignedCompany ?? HOME_COMPANY;
  return {
    id: vehicleSeedId(entry.plateNumber),
    // Not in the handover sheet — the unit fills it in from the vehicle form.
    militaryId: '',
    plateNumber: entry.plateNumber,
    name: entry.model,
    model: entry.model,
    vehicleType: spec.vehicleType,
    category,
    weightCategory: spec.weightCategory,
    // Location alone decides the badge; nothing in the pool came in faulty.
    status: 'available',
    disallowedSoldierIds: [],
    company: HOME_COMPANY_FULL,
    assignedCompany,
    createdAt: SEEDED_AT,
    updatedAt: SEEDED_AT,
  };
}

/** The twenty-eight seeded vehicles: יר"ם first, then the brown pool. */
export const SEED_VEHICLES: readonly Vehicle[] = [
  ...YARAM_POOL.map((entry) => toVehicle(entry, 'yaram')),
  ...BROWN_POOL.map((entry) => toVehicle(entry, 'brown')),
];

/** How many vehicles a clean installation opens with. */
export const SEED_VEHICLE_COUNT = SEED_VEHICLES.length;

/** How many of them stand with the company on day one. */
export const SEED_VEHICLES_AT_HOME = SEED_VEHICLES.filter((vehicle) =>
  isHomeCompany(vehicle.assignedCompany),
).length;

/** How many of them are out with another company on day one. */
export const SEED_VEHICLES_ON_LOAN = SEED_VEHICLE_COUNT - SEED_VEHICLES_AT_HOME;

/** Every plate on the pool — the set the backfill measures against. */
export const POOL_PLATE_KEYS: ReadonlySet<string> = new Set(
  SEED_VEHICLES.map((vehicle) => plateKey(vehicle.plateNumber)),
);

/**
 * The revision of the vehicle pool the local database has been reconciled
 * against. Bump it when the pool itself changes and every device already in the
 * field has to be brought onto it — `reconcileVehicleRoster()` then runs once
 * more, and once only.
 */
export const VEHICLE_ROSTER_REVISION = 1;
